// 카카오맵 API 로드 유틸리티

// 카카오맵 SDK 로드 상태 (싱글톤 패턴)
let isKakaoLoaded = false;
let isKakaoLoading = false;
let loadPromise: Promise<void> | null = null;

// 카카오맵 API 키 가져오기
const getKakaoMapApiKey = (): string => {
  // 환경변수에서 API 키 가져오기
  const apiKey = process.env.REACT_APP_KAKAO_MAP_API_KEY;
  
  console.log('API 키 상태:', {
    fromEnv: !!process.env.REACT_APP_KAKAO_MAP_API_KEY,
    envValue: process.env.REACT_APP_KAKAO_MAP_API_KEY,
    finalKey: apiKey ? `${apiKey.substring(0, 8)}...` : '없음'
  });
  
  if (!apiKey) {
    throw new Error('카카오맵 API 키가 설정되지 않았습니다. .env 파일을 확인해주세요.');
  }
  return apiKey;
};

// 카카오맵 스크립트 URL 생성
const getKakaoMapScriptUrl = (): string => {
  const apiKey = getKakaoMapApiKey();
  return `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${apiKey}&libraries=services&autoload=false`;
};

// 카카오맵 SDK 동적 로드
export const loadKakaoMapSDK = (): Promise<void> => {
  console.log('카카오맵 SDK 로드 시작');
  
  // 이미 완전히 로드된 경우
  if (isKakaoLoaded && window.kakao && window.kakao.maps) {
    console.log('카카오맵이 이미 로드되어 있음');
    return Promise.resolve();
  }

  // 이미 로딩 중인 경우 기존 Promise 반환 (싱글톤)
  if (isKakaoLoading && loadPromise) {
    console.log('카카오맵 로딩이 이미 진행 중');
    return loadPromise;
  }

  // 새로운 로딩 시작
  console.log('새로운 카카오맵 로딩 시작');
  isKakaoLoading = true;
  
  loadPromise = new Promise((resolve, reject) => {
    try {
      // 기존 스크립트 확인
      const existingScript = document.querySelector(`script[src*="dapi.kakao.com"]`);
      if (existingScript) {
        // 기존 스크립트가 있으면 초기화 대기
        waitForKakaoInitialization(resolve, reject);
        return;
      }

      // 새 스크립트 생성 및 로드
      loadNewScript(resolve, reject);
      
    } catch (error) {
      isKakaoLoading = false;
      loadPromise = null;
      reject(error);
    }
  });

  return loadPromise;
};

// 새 스크립트 로드
const loadNewScript = (resolve: () => void, reject: (error: Error) => void): void => {
  const scriptUrl = getKakaoMapScriptUrl();
  console.log('카카오맵 스크립트 URL:', scriptUrl);

  // 스크립트 태그 생성
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = scriptUrl;
  script.async = true;
  
  // 타임아웃 설정 (30초)
  const timeoutId = setTimeout(() => {
    isKakaoLoading = false;
    loadPromise = null;
    reject(new Error('카카오맵 SDK 로드 시간이 초과되었습니다.'));
  }, 30000);

  script.onload = () => {
    clearTimeout(timeoutId);
    script.setAttribute('data-loaded', 'true');
    console.log('카카오맵 스크립트 로드 완료');
    
    // 스크립트 로드 후 초기화 대기
    setTimeout(() => {
      waitForKakaoInitialization(resolve, reject);
    }, 500);
  };

  script.onerror = (error) => {
    clearTimeout(timeoutId);
    isKakaoLoading = false;
    loadPromise = null;
    console.error('카카오맵 스크립트 로드 실패:', error);
    reject(new Error('카카오맵 SDK 로드에 실패했습니다.'));
  };

  // DOM에 추가
  document.head.appendChild(script);
};

// 카카오맵 초기화 대기 함수
const waitForKakaoInitialization = (resolve: () => void, reject: (error: Error) => void): void => {
  // 이미 완전히 로드된 경우
  if (window.kakao && window.kakao.maps && window.kakao.maps.load) {
    try {
      window.kakao.maps.load(() => {
        isKakaoLoaded = true;
        isKakaoLoading = false;
        resolve();
      });
    } catch (error) {
      checkKakaoAvailabilityWithRetry(resolve, reject);
    }
  } else {
    checkKakaoAvailabilityWithRetry(resolve, reject);
  }
};

// 재시도를 포함한 가용성 체크
const checkKakaoAvailabilityWithRetry = (resolve: () => void, reject: (error: Error) => void): void => {
  let attempts = 0;
  const maxAttempts = 30; // 3초 (100ms * 30)
  
  const checkKakao = () => {
    attempts++;
    
    const state = {
      kakao: !!window.kakao,
      maps: !!(window.kakao && window.kakao.maps),
      load: !!(window.kakao && window.kakao.maps && window.kakao.maps.load)
    };
    
    console.log(`카카오맵 초기화 확인 (${attempts}/${maxAttempts}):`, state);
    
    // 모든 조건이 만족되면 성공
    if (state.kakao && state.maps && state.load) {
      console.log('카카오맵 SDK 초기화 성공!');
      isKakaoLoaded = true;
      isKakaoLoading = false;
      resolve();
      return;
    }
    
    // 최대 시도 횟수 도달
    if (attempts >= maxAttempts) {
      isKakaoLoading = false;
      loadPromise = null;
      const errorMsg = `카카오맵 SDK 초기화에 실패했습니다. 상태: kakao=${state.kakao}, maps=${state.maps}, load=${state.load}`;
      console.error(errorMsg);
      console.error('가능한 원인:', [
        '1. 카카오 디벨로퍼스에서 플랫폼 도메인(http://localhost:3000)을 등록하지 않음',
        '2. API 키가 유효하지 않음',
        '3. 네트워크 문제로 스크립트 로드 실패'
      ]);
      reject(new Error(errorMsg));
      return;
    }
    
    // 다음 시도
    setTimeout(checkKakao, 100);
  };
  
  checkKakao();
};

// 카카오맵 로드 상태 확인
export const isKakaoMapLoaded = (): boolean => {
  return isKakaoLoaded && window.kakao && window.kakao.maps;
};

// 카카오맵 로드 대기
export const waitForKakaoMap = (): Promise<void> => {
  if (isKakaoMapLoaded()) {
    return Promise.resolve();
  }
  
  return loadKakaoMapSDK();
};
